import type { RefObject } from 'react';

export type TrackedSection = {
  id: string;
  label: string;
  headingElement: HTMLElement;
  level: number;
  index: number;
};

export function scanHeadings(
  root: HTMLElement,
  headingLevels: number[],
  docId: string,
): TrackedSection[] {
  const headingSelectors = headingLevels.map(level => `h${level}`).join(',');
  let headingElements = Array.from(root.querySelectorAll(headingSelectors));

  if (headingElements.length === 0) {
    headingElements = Array.from(
      root.querySelectorAll('[data-node-type="heading"], [data-content-type="heading"]'),
    );
  }

  const sections: TrackedSection[] = [];
  headingElements.forEach((heading, index) => {
    const level = parseInt(heading.tagName.charAt(1)) || 1;
    if (headingLevels.includes(level)) {
      const label = heading.textContent?.trim() || `Section ${index + 1}`;
      const sectionId = `${docId}:h${level}:${index}`;
      sections.push({ id: sectionId, label, headingElement: heading as HTMLElement, level, index });
    }
  });

  return sections;
}

export function findSectionForElement(
  root: HTMLElement,
  sections: TrackedSection[],
  element: HTMLElement | null,
): TrackedSection | null {
  if (!element || sections.length === 0) return null;
  if (!root.contains(element)) return null;

  const directMatch = sections.find(s => s.headingElement === element || s.headingElement.contains(element));
  if (directMatch) return directMatch;

  let bestMatch: TrackedSection | null = null;
  let bestPosition = -1;
  for (let i = sections.length - 1; i >= 0; i--) {
    const section = sections[i];
    const position = section.headingElement.compareDocumentPosition(element);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
      if (i > bestPosition) {
        bestPosition = i;
        bestMatch = section;
      }
    }
  }
  return bestMatch || sections[0] || null;
}

export function getSectionIdForElement(
  root: HTMLElement,
  sections: TrackedSection[],
  element: HTMLElement | null,
): string | null {
  const s = findSectionForElement(root, sections, element);
  return s?.id || null;
}

export function getSectionTitleForId(
  sections: TrackedSection[],
  sectionId: string | null | undefined,
): string | undefined {
  if (!sectionId) return undefined;
  const s = sections.find(sec => sec.id === sectionId);
  return s?.label;
}

export function getSectionCharCountById(
  root: HTMLElement,
  sections: TrackedSection[],
  sectionId: string,
): number | undefined {
  const sectionIndex = sections.findIndex(s => s.id === sectionId);
  if (sectionIndex === -1) return undefined;

  const currentSection = sections[sectionIndex];
  const nextSection = sections[sectionIndex + 1];

  const range = document.createRange();
  range.setStartAfter(currentSection.headingElement);
  if (nextSection) {
    range.setEndBefore(nextSection.headingElement);
  } else {
    // If it's the last section, range to the end of the root element
    range.setEndAfter(root);
  }

  try {
    const text = range.toString();
    return text.length;
  } catch {
    return undefined;
  }
}

export function getSectionInfoFromSelection(
  root: HTMLElement,
  sections: TrackedSection[],
): { id?: string; title?: string } {
  const selection = window.getSelection?.();
  if (!selection || selection.rangeCount === 0) return {};
  try {
    const range = selection.getRangeAt(0);
    const node = range.commonAncestorContainer;
    let element: HTMLElement | null = null;
    if (node.nodeType === Node.TEXT_NODE) {
      element = (node).parentElement;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      element = node as HTMLElement;
    }
    if (!element) return {};

    const id = getSectionIdForElement(root, sections, element) || undefined;
    const title = getSectionTitleForId(sections, id);
    return { id, title };
  } catch {
    return {};
  }
}

/**
 * Split a selection across multiple sections, returning text and metadata for each section
 */
export function getSelectionBySections(
  root: HTMLElement,
  sections: TrackedSection[],
): Array<{
  sectionId: string;
  sectionTitle: string;
  selectedText: string;
  sectionTotalChars: number;
}> {
  if (sections.length === 0) return [];

  const selection = window.getSelection?.();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return [];

  try {
    const range = selection.getRangeAt(0);

    // Find all sections that intersect with the selection
    const intersectingSections: Array<{
      sectionId: string;
      sectionTitle: string;
      selectedText: string;
      sectionTotalChars: number;
    }> = [];

    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      const nextSection = sections[i + 1];

      // Create a range for this section's content
      const sectionRange = document.createRange();
      sectionRange.setStartAfter(section.headingElement);
      if (nextSection) {
        sectionRange.setEndBefore(nextSection.headingElement);
      } else {
        sectionRange.setEndAfter(root);
      }

      // Check if selection intersects with this section
      const selectionIntersects =
        range.compareBoundaryPoints(Range.START_TO_END, sectionRange) > 0 &&
        range.compareBoundaryPoints(Range.END_TO_START, sectionRange) < 0;

      if (selectionIntersects) {
        // Create an intersection range
        const intersectionRange = document.createRange();

        // Set start to the later of the two starts
        if (range.compareBoundaryPoints(Range.START_TO_START, sectionRange) > 0) {
          intersectionRange.setStart(range.startContainer, range.startOffset);
        } else {
          intersectionRange.setStartAfter(section.headingElement);
        }

        // Set end to the earlier of the two ends
        if (range.compareBoundaryPoints(Range.END_TO_END, sectionRange) < 0) {
          intersectionRange.setEnd(range.endContainer, range.endOffset);
        } else if (nextSection) {
          intersectionRange.setEndBefore(nextSection.headingElement);
        } else {
          intersectionRange.setEndAfter(root);
        }

        const selectedText = intersectionRange.toString();
        const sectionTotalText = sectionRange.toString();

        if (selectedText.length > 0) {
          intersectingSections.push({
            sectionId: section.id,
            sectionTitle: section.label,
            selectedText,
            sectionTotalChars: sectionTotalText.length,
          });
        }
      }
    }

    return intersectingSections;
  } catch (error) {
    console.error('[SectionTracking] Error splitting selection by sections:', error);
    return [];
  }
}

/**
 * Attach a stable analytics bridge on the editor root for other hooks to consume.
 */
export function attachSectionAnalytics(
  root: HTMLElement,
  sectionsRef: RefObject<TrackedSection[]>,
  opts: {
    trackTextSelected?: (sectionId: string | null) => void;
    trackCopy?: (sectionId: string | null) => void;
  } = {},
): void {
  (root as any).__sectionAnalytics = {
    getSectionIdForElement: (element: HTMLElement | null): string | null => {
      const sections = sectionsRef.current || [];
      return getSectionIdForElement(root, sections, element);
    },
    getSectionTitleForId: (sectionId: string | null): string | undefined => {
      const sections = sectionsRef.current || [];
      return getSectionTitleForId(sections, sectionId);
    },
    getSectionInfoFromSelection: (): { id?: string; title?: string } => {
      const sections = sectionsRef.current || [];
      return getSectionInfoFromSelection(root, sections);
    },
    getSectionCharCountById: (sectionId: string): number | undefined => {
      const sections = sectionsRef.current || [];
      return getSectionCharCountById(root, sections, sectionId);
    },
    getSelectionBySections: (): Array<{
      sectionId: string;
      sectionTitle: string;
      selectedText: string;
      sectionTotalChars: number;
    }> => {
      const sections = sectionsRef.current || [];
      return getSelectionBySections(root, sections);
    },
    trackTextSelected: (sectionId: string | null) => {
      if (opts.trackTextSelected) opts.trackTextSelected(sectionId);
    },
    trackCopy: (sectionId: string | null) => {
      if (opts.trackCopy) opts.trackCopy(sectionId);
    },
  };
}


